import { observer } from 'mobx-react-lite';

import { XStack } from '@/components/ui';
import { ScrollMask } from '@/components/ui/ScrollMask';
import { cn } from '@/lib/utils';

import { AgentAvatar } from './agents-avatar';
import { type AgentEntry } from './agents.store';
import { type AgentPendingGroup } from './agents.utils';

type AgentsAttentionStripProps = {
  groups: AgentPendingGroup<AgentEntry>[];
  totalCount: number;
  selectedAgentId: string | undefined;
  onSelect: (agentId: string | undefined) => void;
};

export const AgentsAttentionStrip = observer(
  ({
    groups,
    totalCount,
    selectedAgentId,
    onSelect,
  }: AgentsAttentionStripProps) => {
    return (
      <ScrollMask direction="horizontal" showEndFade className="scrollbar-hide">
        <XStack className="items-start gap-2 py-1.5 pl-[max(0rem,calc((100%-48rem)/2-0.25rem))] pr-6 md:pl-[max(0.5rem,calc((100%-48rem)/2+0.5rem))]">
          <button
            type="button"
            aria-label={`All agents, ${totalCount} pending`}
            aria-pressed={selectedAgentId === undefined}
            className="group/strip-item flex w-20 shrink-0 flex-col items-center gap-2 rounded-lg px-1 py-1 focus-ring"
            onClick={() => onSelect(undefined)}
          >
            <span
              className={cn(
                'flex size-12 items-center justify-center rounded-full bg-surface-hover text-sm font-medium text-foreground-muted group-hover/strip-item:bg-surface-active',
                selectedAgentId === undefined &&
                  'text-foreground ring-2 ring-foreground-muted ring-offset-2 ring-offset-background-subtle'
              )}
            >
              All
            </span>
            <span
              className={cn(
                'max-w-full truncate text-xs leading-4',
                selectedAgentId === undefined
                  ? 'text-foreground'
                  : 'text-foreground-muted'
              )}
            >
              All
            </span>
          </button>
          {groups.map(({ agent, cards }) => {
            if (cards.length === 0 && agent.agentId !== selectedAgentId)
              return null;
            const selected = agent.agentId === selectedAgentId;
            const quiet = cards.length === 0;
            return (
              <button
                key={agent.agentId}
                type="button"
                title={agent.name}
                aria-label={`${agent.name}, ${cards.length} pending`}
                aria-pressed={selected}
                className={cn(
                  'group/strip-item flex w-20 shrink-0 flex-col items-center gap-2 rounded-lg px-1 py-1 hover:opacity-100 focus-ring',
                  quiet && !selected && 'opacity-40'
                )}
                onClick={() => onSelect(agent.agentId)}
              >
                <span
                  className={cn(
                    'relative rounded-full',
                    selected &&
                      'ring-2 ring-foreground-muted ring-offset-2 ring-offset-background-subtle'
                  )}
                >
                  <AgentAvatar bird={agent.bird} className="size-12" />
                  {!quiet && (
                    <span className="absolute -right-1 -top-1 min-w-4 rounded-full bg-foreground-muted px-1 text-center text-[10px] font-semibold leading-4 text-background ring-2 ring-background-subtle">
                      {cards.length}
                    </span>
                  )}
                </span>
                <span
                  className={cn(
                    'max-w-full truncate text-xs leading-4',
                    selected ? 'text-foreground' : 'text-foreground-muted'
                  )}
                >
                  {agent.name}
                </span>
              </button>
            );
          })}
        </XStack>
      </ScrollMask>
    );
  }
);
